import type { TopLevelSpec } from "vega-lite";
import type { BaseChartSpecOptions, FieldType, StackMode } from "../types.js";
import type { Interpolate } from "./line.js";
import { buildTitle, inlineData } from "./shared.js";

export interface AreaChartSpecOptions extends BaseChartSpecOptions {
  /** Field for the x axis (defaults to a temporal scale). */
  x: string;
  /** Field for the y axis. */
  y: string;
  xType?: FieldType;
  yType?: FieldType;
  /** Field that splits the data into multiple stacked/overlaid series. */
  color?: string;
  colorType?: FieldType;
  /** How a multi-series chart stacks. Ignored without a `color` series. */
  stack?: StackMode;
  /** Curve interpolation between points. */
  interpolate?: Interpolate;
  /** Draw a marker at each data point. */
  point?: boolean;
  xTitle?: string;
  yTitle?: string;
}

/** Builds a Vega-Lite area chart spec. */
export function areaChartSpec(options: AreaChartSpecOptions): TopLevelSpec {
  const {
    data,
    x,
    y,
    color,
    colorType = "nominal",
    xType = "temporal",
    yType = "quantitative",
    stack = true,
    interpolate = "linear",
    point = false,
    xTitle,
    yTitle,
    title,
    subtitle,
  } = options;

  const stackMode: "zero" | "normalize" | null = color
    ? stack === "normalize"
      ? "normalize"
      : stack
        ? "zero"
        : null
    : null;
  const titleValue = buildTitle(title, subtitle);

  return {
    data: inlineData(data),
    mark: { type: "area", line: true, interpolate, point, tooltip: true },
    encoding: {
      x: {
        field: x,
        type: xType,
        ...(xTitle !== undefined ? { title: xTitle } : {}),
      },
      y: {
        field: y,
        type: yType,
        ...(yTitle !== undefined ? { title: yTitle } : {}),
        ...(stackMode !== null ? { stack: stackMode } : {}),
      },
      ...(color ? { color: { field: color, type: colorType } } : {}),
    },
    ...(titleValue !== undefined ? { title: titleValue } : {}),
  };
}
